import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalService } from 'meteorite-core';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ConsoleLoggerService } from 'meteorite-shared'

@Injectable({
  providedIn: 'root',
})
export class MeteoriteCartService {
  v1: string; // url for catalog v1
  console: ConsoleLoggerService;
  public cid: string; // checkout/cart ID
  public checkout: {  // incomplete schema
    id: string;
    discountSum: number;
    coupons: any[];
    cart: {
      id: string;
      customer_id: number;
      line_items: {
        physical_items: any[];
        digital_items: any[];
        custom_items: any[];
        gift_certificates: any[];
      };
      coupons: any[];
    };
  };

  constructor(private http: HttpClient, private globalService: GlobalService) {
    // start logs
    this.console = (new ConsoleLoggerService({
      module: 'cart',
      class: this.constructor.name,
      enable: true,
      color: 'orange'
    }));

    // met v1 api url
    this.v1 = this.globalService.catalogApiUrl('v1') + '/bc';
    const cid = localStorage.getItem('cid');
    if (cid) this.fetchCheckout(cid);
  }


  /** Retreives current cart and updates local cart state */
  async fetchCheckout(cid = this.cid, saveCid = true): Promise<any> {
    this.cid = cid;
    return this.http
      .get(this.v1 + '/checkouts/' + cid)
      .toPromise()
      .then((res: { data: any; meta: any }) => {
        this.processCheckout(res.data, saveCid);
      });
  }


  /** Should be called any time checkout or cart are modified to process its changes */
  processCheckout(checkout, saveCid = true): Promise<void> {
    this.console.log('processCheckout', checkout);

    if (checkout) {
      this.checkout = checkout;
      this.checkout.discountSum = this.checkout.coupons.reduce((a: number, b: any) => a + b.discounted_amount, 0);
      this.saveCid(this.checkout.id);
    } else {
      delete this.checkout;
      this.saveCid();
    }
    return Promise.resolve();
  }

  /** Save checkout id persistently */
  saveCid(cid?: string): void {
    if (cid) {
      localStorage.setItem('cid', cid);
    } else {
      localStorage.removeItem('cid');
    }
  }


  updateCart(cart): Promise<void> {
    if (cart) {
      if (this.checkout) {
        this.checkout.cart = cart;
        return this.processCheckout(this.checkout);
      } else {
        // We have the cart but no checkout
        return this.fetchCheckout(cart.id);
      }
    } else {
      return this.processCheckout(null);
    }
  }


  getCheckout(checkoutId = this.cid): Promise<any> {
    return this.http
      .get(this.v1 + '/checkouts/' + checkoutId)
      .toPromise()
      .then((data: { data: any; meta: any }) => data.data);
  }


  /** Adds line items to cart */
  addItems(products: any[], cid = this.cid): Promise<any> {
    const payload: {
      line_items?: any[];
      custom_items?: any[];
      cartId?: string;
    } = {};

    payload.line_items = products.filter((p) => !p.isCustom);
    payload.custom_items = products.filter((p) => p.isCustom);
    if (cid) payload.cartId = cid;

    return this.http
      .post<any>(this.v1 + '/add_to_cart', payload)
      .pipe(tap((data: any) => this.updateCart(data?.data)))
      .toPromise();
  }


  /** Removes a single line item by ID */
  removeItem(itemId: string, cid = this.cid, refresh = true): Promise<any> {
    return this.http
      .get(this.v1 + '/delete_item', { params: { cartId: cid, itemId } })
      .pipe(
        tap((data: any) => {
          if (refresh) this.updateCart(data?.data);
        }),
        catchError((err) => {
          console.error(err);
          return of({});
        })
      )
      .toPromise();
  }


  /** Update quantity and list_price of a line item
   * note: currently, custom_items cannot be updated via BC API, so we have to delete and re-add (this is kinda buggy)
   */
  updateItem(
    item: any,
    qty = item.quantity,
    list_price = item.list_price,
    cid = this.cid
  ): Promise<any> {
    item.quantity = qty;
    item.list_price = list_price;

    // Check if item we're updating is a custom item
    if (
      this.checkout?.cart?.line_items.custom_items.some(
        (custom) => custom === item
      )
    ) {
      return this.removeItem(item.id, cid, false).then(() =>
        this.addItems([{ ...item, isCustom: true }], cid)
      );
    } else {
      return this.http
        .post(this.v1 + '/update_cart_item', { item, cartId: cid })
        .pipe(tap((data: any) => this.updateCart(data?.data)))
        .toPromise();
    }
  }


  addCoupon(cid = this.cid, couponCode): Promise<any> {
    return this.http
      .get<any>(this.v1 + '/addCoupon', {
        params: { cid, couponCode },
      })
      .toPromise()
      .then(this.updateCart);
  }


  removeCoupon(cid = this.cid, couponCode) {
    return this.http
      .get<any>(this.v1 + '/removeCoupon', {
        params: { cid, couponCode },
      })
      .toPromise()
      .then(this.updateCart);
  }


  /** Duplicates a cart and returns it */
  dupCart(cid = this.cid): Promise<any> {
    throw new Error('Not implemented yet');
  }

  loadCart(cid: string): Promise<any> {
    return this.fetchCheckout(cid, false);
  }


  requestQuote(): void {
    let url = '/bulk-add';
    if (this.checkout?.cart.line_items.physical_items) {
      url += '?products=';
      this.checkout?.cart.line_items.physical_items.forEach((item) => {
        url += `${item.product_id}:${item.quantity},`;
      });
      url = url.slice(0, -1); // remove trailing comma
    }
    // this.router.navigateByUrl(url);  // TODO
  }

}
